<template>
  <!--
    The tab that renders this is already gated on the same role, so this guard
    should never be the thing that hides anything. It is here so the form cannot
    be mounted somewhere that forgets the check.
  -->
  <div v-if="isSuperAdmin">
    <div class="row mt-2">
      <div class="col">
        <p>
          Where this provider is told that inspections shared with them have been created or
          changed. They receive the inspection ids and retrieve the records through the
          Connect Copilot API.
        </p>
      </div>
    </div>

    <!--
      The endpoint is only honoured on a Provider Group. The tab is offered
      everywhere so an unexpected hierarchy can still be corrected, but saying
      nothing here would let someone configure a location, see it saved, and
      reasonably conclude it was working.
    -->
    <div v-if="!isProviderGroup" class="row mt-2">
      <div class="col">
        <p class="text-danger">
          This location is not a Provider Group, and notifications are delivered to the
          Provider Group above it. Anything saved here will be stored but not used.
        </p>
      </div>
    </div>

    <!--
      Health first, deliberately. "Is this working" is the question people arrive
      with; the configuration below is what they change once they know the answer.
    -->
    <div v-if="registration" class="row mt-3">
      <div class="col-2">
        <span class="custom-label">Status:</span>
      </div>
      <div class="col">
        <span :class="healthClass">{{ healthText }}</span>
        <span v-if="registration.pending > 0" class="ms-3">
          {{ registration.pending }} waiting to send
        </span>
      </div>
    </div>

    <div v-if="registration && registration.lastError" class="row mt-2">
      <div class="col-2">
        <span class="custom-label">Last error:</span>
      </div>
      <div class="col">{{ registration.lastError }}</div>
    </div>

    <hr v-if="registration" class="mt-3" />

    <div class="row mt-3">
      <div class="col">
        <z-input v-model="url" name="url" label="Endpoint URL"
                 placeholder="https://provider.example.com/opus/notifications"
                 @keyup="dirty = true" @change="dirty = true" />
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-4">
        <z-select v-model="tokenScheme" name="tokenScheme" label="How to send the credential"
                  :options="schemes" @change="onSchemeChange" />
      </div>
      <div v-if="isCustomHeader" class="col-4">
        <z-input v-model="customHeader" name="customHeader" label="Header name"
                 placeholder="X-Api-Key" @keyup="dirty = true" @change="dirty = true" />
      </div>
      <div class="col">
        <z-input v-model="token" name="token" type="password" autocomplete="new-password"
                 :label="tokenLabel" :placeholder="tokenPlaceholder"
                 @keyup="dirty = true" @change="dirty = true" />
      </div>
    </div>

    <div class="row mt-3">
      <div class="col">
        <label>
          <input v-model="isActive" type="checkbox" @change="dirty = true" />
          <span class="ms-2">Send notifications to this provider</span>
        </label>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col">
        <button class="btn btn-primary" :disabled="!canSave" @click="save">
          {{ loading.saving ? 'Saving…' : 'Save' }}
        </button>

        <!--
          Disabled until something is registered, and until unsaved edits are
          committed. Testing what is on screen rather than what is stored would be
          the one result nobody could act on.
        -->
        <button class="btn btn-secondary ms-2" :disabled="!canTest" @click="test">
          {{ loading.testing ? 'Sending…' : 'Send test' }}
        </button>

        <span v-if="dirty" class="ms-3 custom-label">Save before testing.</span>
      </div>
    </div>

    <div v-if="testResult" class="row mt-3">
      <div class="col">
        <div :class="testResult.ok ? 'text-success' : 'text-danger'">
          <strong>{{ testResult.ok ? 'Delivered' : 'Not delivered' }}</strong>
          <span v-if="testResult.statusCode"> — HTTP {{ testResult.statusCode }}</span>
          <span v-if="testResult.durationMs !== undefined"> in {{ testResult.durationMs }}ms</span>
        </div>
        <div v-if="testResult.error" class="mt-1">{{ testResult.error }}</div>
        <div v-if="testResult.ok" class="mt-1 custom-label">
          A test sends an empty batch of type "test", so nothing is recorded against
          this provider and no inspection is implied.
        </div>
      </div>
    </div>

    <!-- The full exchange, so a refusal can be diagnosed here. The credential is masked by the API. -->
    <div v-if="exchange" class="row mt-3">
      <div class="col">
        <ul v-if="exchangeWarnings.length" class="text-danger mb-2">
          <li v-for="(w, i) in exchangeWarnings" :key="i">{{ w }}</li>
        </ul>
        <div v-for="(note, i) in exchange.credentialNotes || []" :key="'n' + i" class="custom-label">{{ note }}</div>
        <div class="custom-label">
          Sent {{ formatTime(exchange.sentOn) }} (UTC {{ exchange.sentOn }}), timeout {{ exchange.timeoutSeconds }}s.
        </div>

        <div class="mt-2">
          <button class="btn btn-sm btn-outline-secondary" @click="copyExchange">
            {{ copied ? 'Copied' : 'Copy request and response' }}
          </button>
        </div>

        <details class="mt-3" open>
          <summary><strong>Request</strong></summary>
          <pre class="exchange">{{ requestText }}</pre>
        </details>

        <details class="mt-2" open>
          <summary><strong>Response</strong></summary>
          <pre v-if="exchange.statusCode" class="exchange">{{ responseText }}</pre>
          <div v-else class="mt-1">No response was received.</div>
          <div v-if="exchange.responseBodyTruncated" class="custom-label">
            Body cut at 32 KB of {{ exchange.responseContentLength || 'an unknown number of' }} bytes.
          </div>
        </details>

        <div v-if="exchange.exception" class="mt-2">
          <span class="custom-label">Exception:</span> {{ exchange.exception }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { authStore } from '@/stores/auth'
import { locationStore } from '@/stores/location'
import { notificationStore } from '@/stores/notification'
import ZInput from '@/components/utilities/elements/Input.vue'
import ZSelect from '@/components/utilities/elements/Select.vue'

const BEARER = 'Bearer'
const CUSTOM = 'custom'

export default {
  name: 'LocationNotification',
  components: {
    ZInput,
    ZSelect
  },
  props: {
    firm: Object
  },
  data() {
    return {
      url: '',
      token: '',
      tokenScheme: BEARER,
      customHeader: '',
      isActive: true,
      dirty: false,
      copied: false
    }
  },
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(locationStore, ['location']),
    ...mapState(notificationStore, ['registration', 'loading', 'testResult']),

    isSuperAdmin() {
      // Matches LocationBasic: role 1 is the only one that may see this.
      return this.profile?.roles?.length ? [1].includes(this.profile.roles[0]) : false
    },

    /*
      FirmClass 4 is 'Provider Group'. The enqueue resolves an opted-in service
      location up to its group and reads the registration from there, so a URL on
      anything else is stored and ignored.
    */
    isProviderGroup() {
      return this.location?.firmClass === 4
    },

    /*
      The LAUNCHPAD firm id of the location being edited.

      Read from the STORE, not from the `firm` prop. That prop is the selected node
      handed down by the tree, which LocationBasic receives as `parent` -- so on an
      add it is the parent rather than the record on screen. Every sibling here
      keys off location.firm.

      A COMPUTED rather than a method so it can be watched: the load has to run when
      the LOCATION arrives, which is not the same moment the prop is set.
    */
    launchpadFirm() {
      return this.location?.firm || null
    },

    /*
      z-select renders option.name, not option.text -- a mismatch shows as a
      dropdown of blank rows that still selects correctly, so it looks like a
      styling problem rather than a data one.
    */
    schemes() {
      return [
        { value: BEARER, name: 'Authorization: Bearer' },
        { value: CUSTOM, name: 'A header of my own' }
      ]
    },

    isCustomHeader() {
      return this.tokenScheme === CUSTOM
    },

    /*
      The token is never returned by the API, so the field starts empty even when
      one is stored. Saying so in the label stops it reading as "no token set".
    */
    hasStoredToken() {
      return !!this.registration?.hasToken
    },

    tokenLabel() {
      return this.hasStoredToken ? 'Credential (stored)' : 'Credential'
    },

    tokenPlaceholder() {
      return this.hasStoredToken ? 'Leave blank to keep the current one' : 'The value the provider gave you'
    },

    canSave() {
      return !this.loading.saving && this.dirty && !!this.url
    },

    /*
      Testing sends to what is STORED, so it is offered only when something is
      stored and there is nothing unsaved that would make the result misleading.
    */
    canTest() {
      return !this.loading.testing && !this.dirty && !!this.registration?.url
    },

    healthText() {
      const r = this.registration
      if (!r) return 'Not registered'
      if (!r.isActive) return 'Registered but switched off'
      if (r.lastSuccessOn && !r.lastFailureOn) return 'Delivering'
      if (r.lastFailureOn && !r.lastSuccessOn) return 'Never delivered successfully'
      if (r.lastSuccessOn && r.lastFailureOn) {
        return new Date(r.lastSuccessOn) >= new Date(r.lastFailureOn)
          ? 'Delivering'
          : 'Failing since the last success'
      }
      return 'Registered, nothing sent yet'
    },

    exchange() {
      return this.testResult?.exchange || null
    },

    // The usual reasons a request that looks right is still refused.
    exchangeWarnings() {
      const x = this.exchange
      if (!x) return []
      const w = []
      if (x.redirected) {
        w.push(`Redirected to ${x.finalMethod} ${x.finalUrl}. The response below is from there, not the registered URL.`)
        if (x.finalMethod !== x.method) w.push(`The redirect turned the ${x.method} into a ${x.finalMethod}, so the body was not delivered.`)
        if (x.credentialDroppedOnRedirect) w.push('The Authorization header is dropped when following a redirect, so the credential never reached the final URL.')
      }
      if (x.url && x.url.startsWith('http://')) w.push('The endpoint is plain http. Many providers refuse or redirect that.')
      return w
    },

    requestText() {
      const x = this.exchange
      if (!x) return ''
      return [
        `${x.method} ${x.url} HTTP/${x.httpVersion}`,
        ...(x.requestHeaders || []).map(h => `${h.name}: ${h.value}`),
        '',
        this.pretty(x.requestBody)
      ].join('\n')
    },

    responseText() {
      const x = this.exchange
      if (!x || !x.statusCode) return ''
      return [
        `HTTP/${x.responseHttpVersion} ${x.statusCode} ${x.reasonPhrase || ''}`.trim(),
        ...(x.responseHeaders || []).map(h => `${h.name}: ${h.value}`),
        '',
        x.responseBody ? this.pretty(x.responseBody) : '(empty body)'
      ].join('\n')
    },

    healthClass() {
      const t = this.healthText
      if (t === 'Delivering') return 'text-success'
      if (t === 'Not registered' || t === 'Registered, nothing sent yet') return ''
      return 'text-danger'
    }
  },
  watch: {
    /*
      WATCHING THE ID, NOT THE `firm` PROP.

      The prop is set before the location is fetched, so an immediate watcher on it
      ran while location was still empty: the load read a null id, returned early,
      and nothing ever fetched again because the prop did not change afterwards.
      The screen simply stayed blank with no error anywhere.

      Watching the resolved id fires when the location actually arrives, and again
      when a different location is selected.
    */
    launchpadFirm: {
      immediate: true,
      handler(id) {
        if (id) this.load()
      }
    }
  },
  methods: {
    /*
      The store is resolved WHERE IT IS USED rather than cached in created().
      An immediate watcher runs before created(), so a property assigned there is
      still undefined on the first load -- which threw inside a handler nobody was
      awaiting, and so vanished without trace.
    */
    store() {
      return notificationStore()
    },

    async load() {
      const id = this.launchpadFirm
      if (!id || !this.isSuperAdmin) return

      await this.store().getRegistration(id)
      const r = this.registration

      this.url = r?.url || ''
      this.isActive = r ? !!r.isActive : true
      this.token = ''

      const scheme = r?.tokenScheme || BEARER
      if (scheme.toLowerCase() === BEARER.toLowerCase()) {
        this.tokenScheme = BEARER
        this.customHeader = ''
      } else {
        this.tokenScheme = CUSTOM
        this.customHeader = scheme
      }
      this.dirty = false
    },



    onSchemeChange() {
      this.dirty = true
      if (!this.isCustomHeader) this.customHeader = ''
    },

    async save() {
      const id = this.launchpadFirm
      if (!id) return

      const payload = {
        launchpadFirm: id,
        url: this.url,
        tokenScheme: this.isCustomHeader ? (this.customHeader || 'X-Api-Key') : BEARER,
        isActive: this.isActive
      }

      // Blank means "keep what is stored". Sending an empty string would read as
      // an instruction to clear it.
      if (this.token) payload.token = this.token

      try {
        await this.store().saveRegistration(payload)
        this.token = ''
        this.dirty = false
        this.$emit('changed')
      } catch (e) {
        // The API validates the URL and answers with a readable reason, which is
        // more use than anything this component could invent.
        alert(e?.response?.data?.error || 'The registration could not be saved.')
      }
    },

    async test() {
      const id = this.launchpadFirm
      if (!id) return
      this.copied = false
      await this.store().sendTest(id)
      // Health may have moved, but a test deliberately does not write to it, so
      // there is nothing to refresh.
    },

    pretty(body) {
      if (!body) return ''
      try {
        return JSON.stringify(JSON.parse(body), null, 2)
      } catch {
        return body
      }
    },

    formatTime(value) {
      return value ? new Date(value).toLocaleString() : ''
    },

    // Plain text, credential already masked, so it can be pasted to the provider.
    async copyExchange() {
      const x = this.exchange
      const text = [
        `Sent ${x.sentOn}`,
        ...this.exchangeWarnings,
        ...(x.credentialNotes || []),
        '',
        '--- Request ---',
        this.requestText,
        '',
        '--- Response ---',
        this.responseText || 'No response was received.',
        x.exception ? `\nException: ${x.exception}` : ''
      ].join('\n')
      try {
        await navigator.clipboard.writeText(text)
        this.copied = true
      } catch {
        alert('The browser blocked copying to the clipboard.')
      }
    }
  },
  emits: ['changed']
}
</script>

<style scoped>
.exchange {
  max-height: 400px;
  overflow: auto;
  padding: 0.75rem;
  margin-top: 0.5rem;
  background: #f6f8fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
